import React, { useState } from 'react';
import { FeedItem, TaskFile } from '../../types';
import { ChatIcon, ClockIcon, ImageIcon, SendIcon, PaperclipIcon } from '../../components/icons/Icons';
import { FILE_PATTERNS } from '../../constants';
import { validateFile } from '../../lib/storageHelpers';

interface FilePreview {
  name: string;
  dataUrl: string;
  file: File;
}

interface ActivityFeedProps {
  activityFeed: FeedItem[];
  taskFiles: TaskFile[];
  newComment: string;
  onNewCommentChange: (value: string) => void;
  onAddComment: (files?: File[]) => void;
  onPreviewAttachment: (att: TaskFile) => void;
  currentUserId?: string;
  isProjectManager?: boolean;
  onEditComment?: (commentId: string, content: string) => Promise<{ success: boolean; error?: string }>;
  onDeleteComment?: (commentId: string) => Promise<{ success: boolean; error?: string }>;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activityFeed,
  taskFiles,
  newComment,
  onNewCommentChange,
  onAddComment,
  onPreviewAttachment,
  currentUserId,
  isProjectManager = false,
  onEditComment,
  onDeleteComment,
}) => {
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  const addFiles = (newFiles: File[]) => {
    const errors: string[] = [];
    const validFiles: File[] = [];
    // Se aceptan documentos además de imágenes: el equipo necesita adjuntar
    // planos y PDFs en los comentarios, no solo fotos. Las imágenes se validan
    // como 'comment_photo' (límite 5 MB) y el resto como 'document' (10 MB).
    newFiles.forEach(file => {
      const kind = file.type.startsWith('image/') ? 'comment_photo' : 'document';
      const err = validateFile(file, kind);
      if (err) errors.push(`${err.file}: ${err.reason}`);
      else validFiles.push(file);
    });
    setFileErrors(errors);
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreviews(prev => [...prev, { name: file.name, dataUrl: reader.result as string, file }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) addFiles(files);
  };

  const canModify = (item: Extract<FeedItem, { type: 'comment' }>) =>
    isProjectManager || (!!currentUserId && item.userId === currentUserId);

  const startEditing = (item: Extract<FeedItem, { type: 'comment' }>) => {
    setEditingId(item.id);
    setEditDraft(item.content);
    setActionError('');
  };

  const saveEdit = async (commentId: string) => {
    if (!onEditComment) return;
    const result = await onEditComment(commentId, editDraft);
    if (!result.success) {
      setActionError(result.error || 'Could not save the change.');
      return;
    }
    setEditingId(null);
    setEditDraft('');
    setActionError('');
  };

  const confirmDelete = async (commentId: string) => {
    if (!onDeleteComment) return;
    const result = await onDeleteComment(commentId);
    if (!result.success) {
      setActionError(result.error || 'Could not delete the comment.');
      return;
    }
    setConfirmDeleteId(null);
    setActionError('');
  };

  const removeFile = (idx: number) => {
    setFilePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleBrowse = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt';
    input.multiple = true;
    input.onchange = (ev: Event) => {
      const files = Array.from((ev.target as HTMLInputElement).files || []);
      if (files.length > 0) addFiles(files);
    };
    input.click();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      // 'file' cubre imágenes y documentos; 'string' es el texto pegado,
      // que debe seguir comportándose como siempre.
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleSend = () => {
    if (!newComment.trim() && filePreviews.length === 0) return;
    const files = filePreviews.length > 0 ? filePreviews.map(fp => fp.file) : undefined;
    onAddComment(files);
    setFilePreviews([]);
    setFileErrors([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getTaskFileById = (id: string): TaskFile | undefined => taskFiles.find(tf => tf.id === id);

  const isImage = (name: string) => FILE_PATTERNS.IMAGE.test(name);

  return (
    <div className="detail-card full-width">
      <h3 className="detail-card-title">
        <ChatIcon size={16} />
        Activity ({activityFeed.length})
      </h3>
      {activityFeed.length > 0 ? (
        <div className="task-comments-list">
          {activityFeed.map(item => {
            if (item.type === 'comment') {
              const modifiable = canModify(item) && (onEditComment || onDeleteComment);
              const isEditing = editingId === item.id;

              return (
                <div key={item.id} className="task-comment-item">
                  <div className="task-comment-header">
                    <strong>{item.userName}</strong>
                    <span className="task-comment-meta">
                      {new Date(item.timestamp).toLocaleString()}
                      {item.editedAt && <span className="comment-edited-tag"> (edited)</span>}
                      {modifiable && !isEditing && (
                        <span className="comment-actions">
                          {onEditComment && (
                            <button
                              type="button"
                              className="comment-action-btn"
                              onClick={() => startEditing(item)}
                              title="Edit comment"
                            >
                              Edit
                            </button>
                          )}
                          {onDeleteComment && (
                            <button
                              type="button"
                              className="comment-action-btn danger"
                              onClick={() => { setConfirmDeleteId(item.id); setActionError(''); }}
                              title="Delete comment"
                            >
                              Delete
                            </button>
                          )}
                        </span>
                      )}
                    </span>
                  </div>

                  {isEditing ? (
                    <div className="comment-edit-form">
                      <textarea
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      {actionError && <div className="status-modal-error">{actionError}</div>}
                      <div className="comment-edit-actions">
                        <button
                          type="button"
                          className="comment-action-btn"
                          onClick={() => { setEditingId(null); setActionError(''); }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="comment-action-btn primary"
                          onClick={() => saveEdit(item.id)}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    item.content && <p>{item.content}</p>
                  )}

                  {confirmDeleteId === item.id && (
                    <div className="comment-delete-confirm">
                      <span>Delete this comment permanently?</span>
                      {actionError && <div className="status-modal-error">{actionError}</div>}
                      <div className="comment-edit-actions">
                        <button
                          type="button"
                          className="comment-action-btn"
                          onClick={() => { setConfirmDeleteId(null); setActionError(''); }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="comment-action-btn danger"
                          onClick={() => confirmDelete(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Comment attachments */}
                  {item.attachments && item.attachments.length > 0 && (
                    <div className="comment-photos">
                      {item.attachments.map(att => (
                        <button
                          key={att.id}
                          className="comment-photo-thumb"
                          onClick={() => onPreviewAttachment(att)}
                          title={att.fileName}
                        >
                          {att.publicUrl && isImage(att.fileName) ? (
                            <img src={att.publicUrl} alt={att.fileName} />
                          ) : (
                            <ImageIcon size={16} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            // Status change item
            const statusAttachments = (item.attachmentIds || [])
              .map(getTaskFileById)
              .filter((tf): tf is TaskFile => tf !== undefined);

            return (
              <div key={item.id} className="task-comment-item task-comment-system">
                <div className="task-comment-header">
                  <strong>
                    <ClockIcon size={12} />
                    {item.changedByName}
                  </strong>
                  <span>{new Date(item.timestamp).toLocaleString()}</span>
                </div>
                <p className="system-change-text">
                  Changed <strong>{item.field}</strong>
                  {item.oldValue && <> from <span className="old-val">{item.oldValue}</span></>}
                  {' '}to <span className="new-val">{item.newValue}</span>
                </p>
                {item.comment && <p className="system-change-comment">{item.comment}</p>}
                {statusAttachments.length > 0 && (
                  <div className="comment-photos">
                    {statusAttachments.map(att => (
                      <button
                        key={att.id}
                        className="comment-photo-thumb"
                        onClick={() => onPreviewAttachment(att)}
                        title={att.fileName}
                      >
                        {att.publicUrl && isImage(att.fileName) ? (
                          <img src={att.publicUrl} alt={att.fileName} />
                        ) : (
                          <span className="activity-photo-link-inner">
                            <ImageIcon size={12} /> View Photo
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="task-no-history">No activity yet.</p>
      )}

      {/* Comment input with file upload */}
      <div
        className={`comment-input-area${dragOver ? ' comment-drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
      >
        {fileErrors.length > 0 && (
          <div className="status-modal-error" style={{ marginBottom: '0.5rem' }}>
            {fileErrors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}
        {filePreviews.length > 0 && (
          <div className="multi-files-preview">
            {filePreviews.map((entry, idx) => (
              <div key={idx} className="multi-file-item">
                {isImage(entry.name) ? (
                  <img src={entry.dataUrl} alt={entry.name} className="multi-file-thumb" />
                ) : (
                  <div className="multi-file-doc">
                    <span className="multi-file-doc-icon">&#128196;</span>
                    <span className="multi-file-doc-name">{entry.name.length > 20 ? entry.name.slice(0, 17) + '...' : entry.name}</span>
                  </div>
                )}
                <button type="button" className="multi-file-remove" onClick={() => removeFile(idx)} title="Remove">&times;</button>
              </div>
            ))}
          </div>
        )}
        <div className="comment-input-row">
          <input
            className="comment-input"
            type="text"
            placeholder="Write a comment..."
            value={newComment}
            onChange={e => onNewCommentChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
          <button className="comment-browse-btn" onClick={handleBrowse} title="Attach images or documents">
            <PaperclipIcon size={16} />
          </button>
          <button
            className="comment-send-btn"
            onClick={handleSend}
            disabled={!newComment.trim() && filePreviews.length === 0}
          >
            <SendIcon />
          </button>
        </div>
        <p className="comment-attach-hint">
          {dragOver ? 'Drop files here…' : 'Drag & drop, paste (Ctrl+V) or use the clip to attach images and documents.'}
        </p>
      </div>
    </div>
  );
};
